import { ApolloCache, ApolloError } from '@apollo/client';

import { describeError, getRateLimitRejection } from '../../../common/rateLimit';
import { describeChangeError, forgetContactData, people, Target } from '../tagging/tagging';

/**
 * Batch update (card 20): NationBuilder's "Batch update" tabs, as far as the CRM
 * holds the data. The form's state, what it sends, what is missing before it can
 * be reviewed, and every word the dialog and the history say. Pure, so it is
 * tested without rendering.
 *
 * API: crmBatchUpdatePreview, crmRunBatchUpdate, crmUndoBatchUpdate,
 * crmBatchUpdates (ilbp-backend docs/CARD_20_BATCH_UPDATE.md).
 */

/** NationBuilder's tabs. People, Consent and Clear field all set contact fields: one change. */
export type BatchTab = 'people' | 'consent' | 'clear' | 'tags' | 'lists' | 'log' | 'memberships';

/** The kind of change a tab makes: the API takes one kind per batch. */
export type BatchGroup = 'fields' | 'tags' | 'lists' | 'log' | 'memberships';

export const TAB_GROUP: Record<BatchTab, BatchGroup> = {
  people: 'fields',
  consent: 'fields',
  clear: 'fields',
  tags: 'tags',
  lists: 'lists',
  log: 'log',
  memberships: 'memberships',
};

export const BATCH_TABS: Array<{ tab: BatchTab; label: string }> = [
  { tab: 'people', label: 'People' },
  { tab: 'tags', label: 'Tags' },
  { tab: 'lists', label: 'Lists' },
  { tab: 'log', label: 'Log contacts' },
  { tab: 'memberships', label: 'Memberships' },
  { tab: 'clear', label: 'Clear field' },
  { tab: 'consent', label: 'Consent' },
];

/** NationBuilder's tabs this CRM has no data for: shown, off, with why. */
export const MISSING_TABS: Array<{ label: string; why: string }> = [
  { label: 'Relationships', why: "NationBuilder's relationships were not migrated: the CRM keeps households only." },
  { label: 'Paths', why: 'Paths (pipelines) are not built yet: plan 4.6 #3.' },
];

/** "" leaves a field as it is: NationBuilder's "Select". */
export type YesNo = '' | 'yes' | 'no';

export interface FieldsForm {
  /** An admin's user id, or "" for no change. */
  pointPerson: string;
  /** "1" to "5", or "". */
  supportLevel: string;
  stage: string;
  doNotCall: YesNo;
  doNotContact: YesNo;
  mobileNumberBad: YesNo;
  emailOptIn: YesNo;
  smsOptIn: YesNo;
  clearPointPerson: boolean;
  clearSupportLevel: boolean;
  clearStage: boolean;
}

export interface LogForm {
  method: string;
  direction: 'OUTBOUND' | 'INBOUND';
  outcome: string;
  notes: string;
  /** datetime-local text; "" for now. */
  occurredAt: string;
  closeFollowUps: boolean;
}

export type MembershipAction = 'ADD' | 'UPDATE' | 'REMOVE';

export interface MembershipForm {
  action: MembershipAction;
  planId: string;
  /** "" for the default (ADD: ACTIVE) or no change (UPDATE). */
  status: string;
  /** YYYY-MM-DD, or "". */
  startedAt: string;
  expiresOn: string;
  clearExpiry: boolean;
}

export interface BatchForm {
  tab: BatchTab;
  fields: FieldsForm;
  tags: { action: 'ADD' | 'REMOVE'; targets: Array<Target> };
  list: { action: 'ADD' | 'REMOVE'; targets: Array<Target> };
  log: LogForm;
  membership: MembershipForm;
  /** Why, kept with the batch. Required when consent is opened. */
  note: string;
  /** YYYY-MM-DD: when consent was given, for a change that opens it. */
  consentGivenAt: string;
}

export const EMPTY_FIELDS: FieldsForm = {
  pointPerson: '',
  supportLevel: '',
  stage: '',
  doNotCall: '',
  doNotContact: '',
  mobileNumberBad: '',
  emailOptIn: '',
  smsOptIn: '',
  clearPointPerson: false,
  clearSupportLevel: false,
  clearStage: false,
};

export const emptyBatchForm = (tab: BatchTab = 'people'): BatchForm => ({
  tab,
  fields: EMPTY_FIELDS,
  tags: { action: 'ADD', targets: [] },
  list: { action: 'ADD', targets: [] },
  log: { method: 'PHONE', direction: 'OUTBOUND', outcome: '', notes: '', occurredAt: '', closeFollowUps: false },
  membership: { action: 'ADD', planId: '', status: '', startedAt: '', expiresOn: '', clearExpiry: false },
  note: '',
  consentGivenAt: '',
});

/** NationBuilder's support levels, with what each means there. */
export const SUPPORT_LEVELS: Array<{ value: string; label: string }> = [
  { value: '1', label: '1 · Strong support' },
  { value: '2', label: '2 · Leaning support' },
  { value: '3', label: '3 · Undecided' },
  { value: '4', label: '4 · Leaning against' },
  { value: '5', label: '5 · Strong opposition' },
];

export const METHODS: Array<{ value: string; label: string }> = [
  { value: 'PHONE', label: 'Phone call' },
  { value: 'SMS', label: 'Text message' },
  { value: 'EMAIL', label: 'Email' },
  { value: 'WHATSAPP', label: 'WhatsApp' },
  { value: 'IN_PERSON', label: 'In person' },
  { value: 'OTHER', label: 'Other' },
];

/** What happened. UNKNOWN is kept for migrated calls and is never offered. */
export const OUTCOMES: Array<{ value: string; label: string }> = [
  { value: 'SPOKE', label: 'Spoke with them' },
  { value: 'ANSWERED', label: 'Answered' },
  { value: 'LEFT_MESSAGE', label: 'Left a message' },
  { value: 'NOT_HOME', label: 'Not home / no answer' },
  { value: 'BAD_INFO', label: 'Bad information' },
  { value: 'REFUSED', label: 'Refused' },
  { value: 'INBOUND', label: 'They got in touch' },
  { value: 'SEND_EMAIL', label: 'Sent an email' },
  { value: 'SEND_TEXT', label: 'Sent a text' },
];

export const MEMBERSHIP_STATUSES: Array<{ value: string; label: string }> = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'GRACE_PERIOD', label: 'Grace period' },
  { value: 'EXPIRED', label: 'Expired' },
  { value: 'CANCELED', label: 'Canceled' },
];

/** The API's limits (crm-batch-updates.ts in ilbp-backend). */
export const BATCH_LIMITS = { stage: 50, note: 1000, notes: 10_000 } as const;

/* ------------------------------------------------------------------ *
 * What the form sends
 * ------------------------------------------------------------------ */

/** The change input: one of fields, tags, list, logContact, membership, with the note. */
export interface BatchChangeInput {
  fields?: Record<string, unknown>;
  tags?: { action: 'ADD' | 'REMOVE'; tagIds: Array<number> };
  list?: { action: 'ADD' | 'REMOVE'; listId: number };
  logContact?: Record<string, unknown>;
  membership?: Record<string, unknown>;
  note?: string;
  consentGivenAt?: string;
}

const yes = (value: YesNo) => (value === '' ? undefined : value === 'yes');

/** The fields part: only what is set, a clear winning over a value. */
export const fieldsInput = (f: FieldsForm): Record<string, unknown> => {
  const out: Record<string, unknown> = {};
  if (f.clearPointPerson) out.pointPerson = { clear: true };
  else if (f.pointPerson) out.pointPerson = { set: Number(f.pointPerson) };
  if (f.clearSupportLevel) out.supportLevel = { clear: true };
  else if (f.supportLevel) out.supportLevel = { set: Number(f.supportLevel) };
  if (f.clearStage) out.stage = { clear: true };
  else if (f.stage.trim()) out.stage = { set: f.stage.trim().replace(/\s+/g, ' ') };
  for (const key of ['emailOptIn', 'smsOptIn', 'doNotCall', 'doNotContact', 'mobileNumberBad'] as const) {
    const value = yes(f[key]);
    if (value !== undefined) out[key] = value;
  }
  return out;
};

/** Opening email or texts, or lifting do not call or do not contact: the API wants to know how. */
export const opensConsent = (f: FieldsForm) =>
  f.emailOptIn === 'yes' || f.smsOptIn === 'yes' || f.doNotCall === 'no' || f.doNotContact === 'no';

/** A datetime-local value as ISO, in the browser's own zone. */
const isoOf = (local: string) => new Date(local).toISOString();

/** The change the active tab makes, as the API takes it. Call only when `formProblems` is empty. */
export const changeInput = (form: BatchForm): BatchChangeInput => {
  const note = form.note.trim() || undefined;
  switch (TAB_GROUP[form.tab]) {
    case 'fields': {
      const consent = opensConsent(form.fields);
      return {
        fields: fieldsInput(form.fields),
        ...(note ? { note } : {}),
        ...(consent && form.consentGivenAt ? { consentGivenAt: isoOf(`${form.consentGivenAt}T12:00`) } : {}),
      };
    }
    case 'tags':
      return {
        tags: { action: form.tags.action, tagIds: form.tags.targets.map((t) => t.id as number) },
        ...(note ? { note } : {}),
      };
    case 'lists':
      return { list: { action: form.list.action, listId: form.list.targets[0].id as number }, ...(note ? { note } : {}) };
    case 'log': {
      const l = form.log;
      return {
        logContact: {
          method: l.method,
          direction: l.direction,
          outcome: l.outcome,
          ...(l.notes.trim() ? { notes: l.notes.trim() } : {}),
          ...(l.occurredAt ? { occurredAt: isoOf(l.occurredAt) } : {}),
          closeFollowUps: l.closeFollowUps,
        },
        ...(note ? { note } : {}),
      };
    }
    case 'memberships': {
      const m = form.membership;
      const membership: Record<string, unknown> = { action: m.action, planId: Number(m.planId) };
      if (m.action !== 'REMOVE') {
        if (m.status) membership.status = m.status;
        if (m.startedAt) membership.startedAt = isoOf(`${m.startedAt}T12:00`);
        if (m.action === 'UPDATE' && m.clearExpiry) membership.expiresOn = { clear: true };
        else if (m.expiresOn) membership.expiresOn = { set: m.expiresOn };
      }
      return { membership, ...(note ? { note } : {}) };
    }
  }
};

/** What is missing or wrong before the change can be reviewed, in words. Empty when it can. */
export const formProblems = (form: BatchForm, now = new Date()): Array<string> => {
  const problems: Array<string> = [];
  if (form.note.trim().length > BATCH_LIMITS.note) problems.push(`The note is at most ${BATCH_LIMITS.note} characters.`);
  switch (TAB_GROUP[form.tab]) {
    case 'fields': {
      const f = form.fields;
      if (!Object.keys(fieldsInput(f)).length) problems.push('Choose at least one field to change.');
      if (!f.clearStage && f.stage.trim().length > BATCH_LIMITS.stage) {
        problems.push(`A stage is at most ${BATCH_LIMITS.stage} characters.`);
      }
      if (opensConsent(f) && !form.note.trim()) {
        problems.push('This opens email, texts or calls to people who had them closed. Say in the note how they gave consent.');
      }
      if (form.consentGivenAt && new Date(`${form.consentGivenAt}T00:00`).getTime() > now.getTime()) {
        problems.push('Consent cannot have been given in the future.');
      }
      break;
    }
    case 'tags':
      if (!form.tags.targets.length) problems.push(`Choose the tags to ${form.tags.action === 'ADD' ? 'add' : 'remove'}.`);
      break;
    case 'lists':
      if (!form.list.targets.length) problems.push('Choose the list.');
      break;
    case 'log': {
      const l = form.log;
      if (!l.outcome) problems.push('Say what happened.');
      if (l.notes.trim().length > BATCH_LIMITS.notes) problems.push(`Notes are at most ${BATCH_LIMITS.notes.toLocaleString('en-US')} characters.`);
      if (l.occurredAt && new Date(l.occurredAt).getTime() > now.getTime() + 5 * 60_000) {
        problems.push('A contact cannot be logged before it happens.');
      }
      break;
    }
    case 'memberships': {
      const m = form.membership;
      if (!m.planId) problems.push('Choose the membership plan.');
      if (m.action === 'UPDATE' && !m.status && !m.startedAt && !m.expiresOn && !m.clearExpiry) {
        problems.push('Say what to change: the status, the start or the expiry.');
      }
      break;
    }
  }
  return problems;
};

/** Which tabs of the active kind's group carry edits: a dot on the tab. */
export const tabHasEdits = (form: BatchForm, tab: BatchTab): boolean => {
  const f = form.fields;
  switch (tab) {
    case 'people':
      return Boolean(f.pointPerson || f.supportLevel || f.stage.trim() || f.doNotCall || f.doNotContact || f.mobileNumberBad);
    case 'consent':
      return Boolean(f.emailOptIn || f.smsOptIn);
    case 'clear':
      return f.clearPointPerson || f.clearSupportLevel || f.clearStage;
    case 'tags':
      return form.tags.targets.length > 0;
    case 'lists':
      return form.list.targets.length > 0;
    case 'log':
      return Boolean(form.log.outcome);
    case 'memberships':
      return Boolean(form.membership.planId);
  }
};

/** Edits on another kind of change, which a batch on this tab will not make. */
export const otherGroupEdits = (form: BatchForm): Array<string> => {
  const here = TAB_GROUP[form.tab];
  const labels = new Set<string>();
  for (const { tab, label } of BATCH_TABS) {
    if (TAB_GROUP[tab] !== here && tabHasEdits(form, tab)) labels.add(TAB_GROUP[tab] === 'fields' ? 'People, Consent or Clear field' : label);
  }
  return Array.from(labels);
};

/** Removing, taking off, clearing or opening consent: the confirm button is red. */
export const isRisky = (form: BatchForm): boolean => {
  const f = form.fields;
  switch (TAB_GROUP[form.tab]) {
    case 'fields':
      return opensConsent(f) || f.clearPointPerson || f.clearSupportLevel || f.clearStage;
    case 'tags':
      return form.tags.action === 'REMOVE';
    case 'lists':
      return form.list.action === 'REMOVE';
    case 'memberships':
      return form.membership.action === 'REMOVE';
    default:
      return false;
  }
};

/* ------------------------------------------------------------------ *
 * Words
 * ------------------------------------------------------------------ */

const n = (value: number) => value.toLocaleString('en-US');

/** The confirm button: "Update 4,963 contacts", "Log 3 contacts". */
export const confirmBatchLabel = (form: BatchForm, count: number) =>
  `${TAB_GROUP[form.tab] === 'log' ? 'Log for' : 'Update'} ${people(count)}`;

export interface BatchPart {
  key: string;
  label: string;
  changed: number;
  unchanged: number | null;
}

export interface BatchPreview {
  matched: number;
  willChange: number;
  skipped: number;
  parts: Array<BatchPart>;
  warnings: Array<string>;
}

/** The line above the confirm button. */
export const describePreview = (preview: BatchPreview): string => {
  if (preview.matched === 0) return 'Nobody is selected: there is nothing to change.';
  if (preview.willChange === 0) return `None of the ${people(preview.matched)} would change: every one already has these values.`;
  const left = preview.skipped ? ` ${n(preview.skipped)} will be left out.` : '';
  const already = preview.matched - preview.skipped - preview.willChange;
  const rest = already > 0 ? ` ${already === 1 ? 'One already has' : `${n(already)} already have`} these values.` : '';
  if (preview.willChange === preview.matched) return `All ${people(preview.matched)} will change.`;
  return `${n(preview.willChange)} of the ${people(preview.matched)} will change.${left}${rest}`;
};

export interface Admin {
  id: number;
  firstName: string | null;
  lastName: string | null;
}

export const adminName = (admin: Admin | null | undefined) =>
  admin ? [admin.firstName, admin.lastName].filter(Boolean).join(' ').trim() || `admin #${admin.id}` : '';

export type BatchStatus = 'RUNNING' | 'DONE' | 'FAILED' | 'UNDOING' | 'UNDONE';

export interface BatchRecord {
  id: number;
  kind: string;
  status: BatchStatus;
  /** People done so far; `matched` once DONE. */
  processed: number;
  undoProcessed: number | null;
  error: string | null;
  finishedAt: string | null;
  canResume: boolean;
  summary: string;
  matched: number;
  changed: number;
  skipped: number;
  parts: Array<BatchPart>;
  note: string | null;
  consentGivenAt: string | null;
  segmentId: number | null;
  createdAt: string;
  undoneAt: string | null;
  undoRestored: number | null;
  undoKept: number | null;
  canUndo: boolean;
  undoNote: string;
  createdBy: Admin | null;
  undoneBy: Admin | null;
  segment: { id: number; name: string; type: string } | null;
}

export const KIND_LABELS: Record<string, string> = {
  FIELDS: 'Fields',
  TAGS_ADD: 'Tags added',
  TAGS_REMOVE: 'Tags removed',
  LIST_ADD: 'Added to a list',
  LIST_REMOVE: 'Taken off a list',
  LOG_CONTACT: 'Contact logged',
  MEMBERSHIP_ADD: 'Memberships added',
  MEMBERSHIP_UPDATE: 'Memberships changed',
  MEMBERSHIP_REMOVE: 'Memberships removed',
};

/** "4,963 selected · 1,204 changed · 12 left out". */
export const batchCounts = (b: Pick<BatchRecord, 'matched' | 'changed' | 'skipped'>) =>
  [`${n(b.matched)} selected`, `${n(b.changed)} changed`, b.skipped ? `${n(b.skipped)} left out` : ''].filter(Boolean).join(' · ');

/** What an undo did: "Put back for 1,202; 2 changed since and kept as they are." */
export const describeUndo = (b: Pick<BatchRecord, 'undoRestored' | 'undoKept'>) => {
  const restored = b.undoRestored ?? 0;
  const kept = b.undoKept ?? 0;
  return `Put back for ${people(restored)}${kept ? `; ${n(kept)} changed since and kept as they are` : ''}.`;
};

/* ------------------------------------------------------------------ *
 * Errors
 * ------------------------------------------------------------------ */

const firstError = (error: unknown) => (error instanceof ApolloError ? error.graphQLErrors?.[0] : undefined);

/** What to tell the admin when a preview, a batch or an undo is refused. */
export const describeBatchError = (error: unknown): string => {
  if (getRateLimitRejection(error)) return describeError(error, 'Too many batch updates');
  const first = firstError(error);
  switch (first?.extensions?.code) {
    case 'CRM_BATCH_BUSY':
      return 'Another batch update is running. Nothing was changed. Try again in a moment.';
    case 'CRM_BATCH_NOT_UNDOABLE':
      return first.message || 'This batch update cannot be undone.';
    case 'CRM_BATCH_UPDATE_NOT_FOUND':
      return 'That batch update no longer exists.';
    case 'CRM_MEMBERSHIP_PLAN_NOT_FOUND':
      return 'That membership plan no longer exists. Close this and open it again.';
    case 'CRM_QUERY_TIMEOUT':
      return 'The batch update took too long and was stopped. Nothing was changed. Narrow the selection and try again.';
    case 'FORBIDDEN':
      return 'Only admins can run batch updates.';
    default:
      return describeChangeError(error);
  }
};

/** After a batch or an undo: everything the cache holds about these people, and the history. */
export const forgetBatchData = (cache: ApolloCache<unknown>) => {
  forgetContactData(cache, 'selection');
  ['crmBatchUpdates', 'crmBatchUpdate', 'crmMembershipPlans', 'crmSegments', 'crmList'].forEach((fieldName) =>
    cache.evict({ id: 'ROOT_QUERY', fieldName }),
  );
  cache.gc();
};

/** Still working: the change or the undo goes on in the background. */
export const isWorking = (b: Pick<BatchRecord, 'status'>) => b.status === 'RUNNING' || b.status === 'UNDOING';

/** How far a working batch has got: "40,000 of 195,742 (20%)". */
export const progressOf = (b: Pick<BatchRecord, 'status' | 'processed' | 'undoProcessed' | 'matched'>) => {
  const done = b.status === 'UNDOING' ? b.undoProcessed ?? 0 : b.processed;
  const percent = b.matched ? Math.floor((done / b.matched) * 100) : 100;
  return { done, percent, text: `${n(done)} of ${n(b.matched)} (${percent}%)` };
};

/** How often a screen asks for a working batch's progress. Only while one is shown working. */
export const PROGRESS_POLL_MS = 2000;
